import { useLogo } from 'core/common/hooks';
import { prepareHtml } from 'core/common/utils/prepareHtml';
import { SectionBlurredReportAttributes } from 'core/flow/entities';
import { ButtonAnimation } from 'core/theme/ui/button';
import { UserProfile } from 'core/user/entities';
import { getZodiacPersonImage } from 'core/zodiac/utils/getZodiacPersonImage';
import {
  TitleStyled,
  AccessDescriptionStyled,
  BlurInnerContainerStyled,
  BlurOuterContainerStyled,
  WrapperStyled,
  ContentBlockStyled,
  ContentTextStyled,
  ContentTitleStyled,
  ItemStyled,
  LogoImageStyled,
  LogoImageBlockStyled,
  NumberedListStyled,
  UserInfoBlockStyled,
  ZodiacImageStyled,
  ZodiacImageBlockStyled,
  ButtonStyled,
} from './styled';

type BlurredReportProps = {
  content: SectionBlurredReportAttributes['content'];
  userPreferences: UserProfile;
  onScrollToBlock: () => void;
};

export const BlurredReport = ({
  content,
  userPreferences,
  onScrollToBlock,
}: BlurredReportProps) => {
  const { logoSrcBig } = useLogo();

  const zodiacImage = getZodiacPersonImage(userPreferences.zodiac, userPreferences.gender);
  const { readingSummary, readingDescription, buttonText, accessDescription } = content;
  const { list, readingSummaryTitle } = readingSummary || {};
  const { readingDescriptionTitle, readingDescriptionText } = readingDescription || {};

  return (
    <WrapperStyled>
      <TitleStyled variant="h1" dangerouslySetInnerHTML={prepareHtml(content.title)} />
      <UserInfoBlockStyled>
        <LogoImageBlockStyled>
          <LogoImageStyled width={140} height={140} src={logoSrcBig.src} alt="Nebula logo" />
        </LogoImageBlockStyled>
        <ZodiacImageBlockStyled>
          <ZodiacImageStyled width={72} height={146} src={zodiacImage} alt="Zodiac sign" />
        </ZodiacImageBlockStyled>
      </UserInfoBlockStyled>
      <ContentBlockStyled>
        {readingSummary && (
          <>
            <ContentTitleStyled
              variant="h3"
              dangerouslySetInnerHTML={prepareHtml(readingSummaryTitle)}
            />
            {list && (
              <NumberedListStyled>
                {list.map((text) => (
                  <ItemStyled key={text}>
                    <ContentTextStyled
                      variant="bodyMediumSemiBold"
                      dangerouslySetInnerHTML={prepareHtml(text)}
                    />
                  </ItemStyled>
                ))}
              </NumberedListStyled>
            )}
          </>
        )}
        {readingDescription && (
          <>
            <ContentTitleStyled
              variant="h3"
              dangerouslySetInnerHTML={prepareHtml(readingDescriptionTitle)}
            />
            <ContentTextStyled
              variant="bodyMediumSemiBold"
              dangerouslySetInnerHTML={prepareHtml(readingDescriptionText)}
            />
          </>
        )}
      </ContentBlockStyled>
      <BlurOuterContainerStyled>
        <BlurInnerContainerStyled>
          <AccessDescriptionStyled
            variant="bodyLargeSemiBold"
            dangerouslySetInnerHTML={prepareHtml(accessDescription)}
          />
          <ButtonStyled
            animation={ButtonAnimation.SHINE}
            variant="contained"
            onClick={onScrollToBlock}
          >
            {buttonText}
          </ButtonStyled>
        </BlurInnerContainerStyled>
      </BlurOuterContainerStyled>
    </WrapperStyled>
  );
};
